import { useEffect, useState } from 'react';
import { useAppSelector } from '../store';
import { API_BASE_URL } from '../lib/utils';

export function usePushSubscription() {
  const { token, user } = useAppSelector((state) => state.auth);
  const [vapidPublicKey, setVapidPublicKey] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<PushSubscription | null>(null);

  useEffect(() => {
    if (!token) return;

    // Fetch VAPID public key from backend
    console.log('Fetching VAPID public key...');
    fetch(`${API_BASE_URL}/push/vapid-public-key`)
      .then((res) => res.json())
      .then((data) => {
        console.log('VAPID public key received:', data.publicKey ? 'success' : 'failed');
        if (data.publicKey) {
          setVapidPublicKey(data.publicKey);
        }
      })
      .catch((err) => console.error('Failed to fetch VAPID key:', err));
  }, [token]);

  useEffect(() => {
    if (!vapidPublicKey || !('serviceWorker' in navigator) || !user) {
      console.log('Push subscription prerequisites not met:', {
        hasVapidKey: !!vapidPublicKey,
        hasServiceWorker: 'serviceWorker' in navigator,
        hasUser: !!user,
      });
      return;
    }

    let registration: ServiceWorkerRegistration | null = null;

    const registerAndSubscribe = async () => {
      try {
        console.log('Registering service worker...');
        registration = await navigator.serviceWorker.register('/sw.js');
        console.log('Service Worker registered successfully');

        // Check if we already have a subscription
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          console.log('Existing subscription found:', existingSubscription.endpoint);
          setSubscription(existingSubscription);
          // Send to backend to ensure it's up to date
          await sendSubscriptionToBackend(existingSubscription);
          return;
        }

        console.log('Requesting new push subscription...');
        // Request new subscription
        const newSubscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as any,
        });

        console.log('New subscription created:', newSubscription.endpoint);
        setSubscription(newSubscription);
        await sendSubscriptionToBackend(newSubscription);
      } catch (err) {
        console.error('Push subscription error:', err);
      }
    };

    registerAndSubscribe();

    return () => {
      // Cleanup on unmount
      if (registration) {
        registration.pushManager.getSubscription().then((sub) => {
          if (sub) {
            sub.unsubscribe().catch(() => {});
          }
        });
      }
    };
  }, [vapidPublicKey, user, token]);

  const sendSubscriptionToBackend = async (sub: PushSubscription) => {
    try {
      console.log('Sending subscription to backend for user:', user?.id);
      const response = await fetch(`${API_BASE_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ subscription: sub }),
      });
      console.log('Subscription backend response:', response.status);
    } catch (err) {
      console.error('Failed to send subscription to backend:', err);
    }
  };

  const unsubscribe = async () => {
    if (subscription) {
      try {
        await subscription.unsubscribe();
        await fetch(`${API_BASE_URL}/push/unsubscribe`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        setSubscription(null);
      } catch (err) {
        console.error('Failed to unsubscribe:', err);
      }
    }
  };

  return { subscription, unsubscribe };
}

// Helper to convert base64 string to Uint8Array
function urlBase64ToUint8Array(base64String: string): Uint8Array {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray as any;
}
